import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, startOfDay, endOfDay, subDays, subMonths, isWithinInterval } from "date-fns";
import { de } from "date-fns/locale";
import { 
  Search, RefreshCw, Eye, EyeOff, Download, FileText, User, 
  Plus, Pencil, Trash2, Upload, Clock, DollarSign, Gift, 
  Shirt, Wrench, Shield, CalendarIcon, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DateRange } from "react-day-picker";

// Document Access Log Types
interface DocumentAccessLogEntry {
  id: string;
  user_id: string;
  user_role: string | null;
  document_id: string;
  document_type: string;
  action: string;
  source: string;
  user_agent: string | null;
  created_at: string;
}

// Entity Audit Log Types
interface EntityAuditLogEntry {
  id: string;
  user_id: string | null;
  user_role: string | null;
  entity_type: string;
  entity_id: string;
  employee_id: string | null;
  action: string;
  changes: unknown;
  created_at: string;
}

interface UserProfile {
  id: string;
  full_name: string | null;
}

interface Employee {
  id: string;
  name: string;
}

type SortField = 'created_at' | 'user_role' | 'action' | 'document_type' | 'source';
type SortDirection = 'asc' | 'desc';

// Mapping von Datenbank-Feldnamen zu lesbaren deutschen Bezeichnungen
const FIELD_LABEL_MAP: Record<string, { label: string; description?: string }> = {
  // Mitarbeiter-Stammdaten (employees)
  name: { label: "Name", description: "Vor- und Nachname" },
  first_name: { label: "Vorname", description: "Vorname des Mitarbeiters" },
  last_name: { label: "Nachname", description: "Nachname des Mitarbeiters" },
  position: { label: "Position", description: "Stellenbezeichnung" },
  department: { label: "Abteilung", description: "Zugeordnete Abteilung" },
  employee_number: { label: "Personalnummer", description: "Interne Mitarbeiternummer" },
  start_date: { label: "Eintrittsdatum", description: "Beginn des Arbeitsverhältnisses" },
  end_date: { label: "Austrittsdatum", description: "Ende des Arbeitsverhältnisses" },
  birth_date: { label: "Geburtsdatum", description: "Geburtsdatum des Mitarbeiters" },
  business_email: { label: "Geschäfts-E-Mail", description: "Dienstliche E-Mail-Adresse" },
  private_email: { label: "Private E-Mail", description: "Private E-Mail-Adresse" },
  phone: { label: "Telefon", description: "Telefonnummer" },
  mobile: { label: "Mobil", description: "Mobiltelefonnummer" },
  address_street: { label: "Straße", description: "Straße und Hausnummer" },
  address_zip: { label: "PLZ", description: "Postleitzahl" },
  address_city: { label: "Ort", description: "Stadt/Gemeinde" },
  address_country: { label: "Land", description: "Land" },
  nationality: { label: "Nationalität", description: "Staatsangehörigkeit" },
  marital_status: { label: "Familienstand", description: "Familienstand" },
  children_count: { label: "Anzahl Kinder", description: "Anzahl der Kinder" },
  profile_image_url: { label: "Profilbild", description: "Profilbild-URL" },
  drivers_license: { label: "Führerschein", description: "Führerscheinklassen" },
  drivers_license_classes: { label: "FS-Klassen", description: "Führerscheinklassen" },
  drivers_license_expiry: { label: "FS-Ablauf", description: "Ablaufdatum Führerschein" },
  tax_class: { label: "Steuerklasse", description: "Lohnsteuerklasse" },
  tax_id: { label: "Steuer-ID", description: "Steueridentifikationsnummer" },
  social_security_number: { label: "SV-Nummer", description: "Sozialversicherungsnummer" },
  health_insurance_name: { label: "Krankenkasse", description: "Name der Krankenkasse" },
  health_insurance_number: { label: "KK-Nummer", description: "Krankenversicherungsnummer" },
  iban: { label: "IBAN", description: "Kontonummer (IBAN)" },
  bic: { label: "BIC", description: "Bankleitzahl (BIC)" },
  bank_name: { label: "Bank", description: "Name der Bank" },
  emergency_contact_name: { label: "Notfallkontakt", description: "Name des Notfallkontakts" },
  emergency_contact_phone: { label: "Notfall-Tel.", description: "Telefon des Notfallkontakts" },
  notes: { label: "Notizen", description: "Interne Notizen" },
  archived_at: { label: "Archiviert am", description: "Archivierungszeitpunkt" },
  archived_by: { label: "Archiviert von", description: "Archiviert durch Benutzer" },
  
  // Gehalt (salary_history)
  base_salary: { label: "Grundgehalt", description: "Monatliches Grundgehalt" },
  currency: { label: "Währung", description: "Währung (EUR)" },
  effective_date: { label: "Gültig ab", description: "Datum der Gehaltsänderung" },
  reason: { label: "Grund", description: "Grund der Änderung" },
  salary_type: { label: "Gehaltsart", description: "Monatsgehalt/Stundenlohn" },
  hourly_rate: { label: "Stundenlohn", description: "Stundenlohn bei Stundenbasis" },
  weekly_hours: { label: "Wochenstunden", description: "Wöchentliche Arbeitszeit" },
  
  // Bonus (bonus_history)
  amount: { label: "Betrag", description: "Bonusbetrag" },
  bonus_type: { label: "Bonusart", description: "Art des Bonus" },
  bonus_date: { label: "Bonusdatum", description: "Datum der Bonuszahlung" },
  description: { label: "Beschreibung", description: "Beschreibung des Bonus" },
  period_start: { label: "Zeitraum von", description: "Beginn des Bonuszeitraums" },
  period_end: { label: "Zeitraum bis", description: "Ende des Bonuszeitraums" },
  target_achieved: { label: "Zielerreichung", description: "Zielerreichung in Prozent" },
  approved_by: { label: "Genehmigt von", description: "Genehmigt durch" },
  approved_at: { label: "Genehmigt am", description: "Genehmigungsdatum" },
  paid_date: { label: "Auszahlungsdatum", description: "Datum der Auszahlung" },
  
  // Benefits (benefits_history)
  benefit_type: { label: "Benefit-Art", description: "Art des Benefits" },
  benefit_name: { label: "Benefit-Name", description: "Name des Benefits" },
  monthly_value: { label: "Monatswert", description: "Monatlicher Wert" },
  provider: { label: "Anbieter", description: "Anbieter des Benefits" },
  status: { label: "Status", description: "Aktiv/Inaktiv" },
  payment_frequency: { label: "Zahlungsturnus", description: "Zahlungshäufigkeit" },
  details: { label: "Details", description: "Zusätzliche Details" },
  
  // Dokumente (employee_documents)
  file_name: { label: "Dateiname", description: "Name der Datei" },
  file_path: { label: "Dateipfad", description: "Speicherpfad" },
  file_size: { label: "Dateigröße", description: "Größe in Bytes" },
  mime_type: { label: "Dateityp", description: "MIME-Typ der Datei" },
  category: { label: "Kategorie", description: "Dokumentenkategorie" },
  category_id: { label: "Kategorie-ID", description: "ID der Kategorie" },
  uploaded_by: { label: "Hochgeladen von", description: "Hochgeladen durch" },
  
  // Kleidung (employee_clothing)
  tshirt_size: { label: "T-Shirt-Größe", description: "T-Shirt-Größe" },
  pants_size: { label: "Hosengröße", description: "Größe der Arbeitshose" },
  work_shoes_size: { label: "Schuhgröße", description: "Größe der Arbeitsschuhe" },
  winter_jacket_size: { label: "Winterjacke", description: "Größe der Winterjacke" },
  fleece_jacket_size: { label: "Fleecejacke", description: "Größe der Fleecejacke" },
  sweatshirt_size: { label: "Sweatshirt", description: "Größe des Sweatshirts" },
  helmet_size: { label: "Helmgröße", description: "Kopfumfang für Helm" },
  gloves_size: { label: "Handschuhe", description: "Handschuhgröße" },
  safety_glasses: { label: "Schutzbrille", description: "Typ der Schutzbrille" },
  ear_protection: { label: "Gehörschutz", description: "Typ des Gehörschutzes" },
  signature_url: { label: "Unterschrift", description: "Unterschrift des Mitarbeiters" },
  signed_at: { label: "Unterschrieben am", description: "Datum der Unterschrift" },
  signed_by: { label: "Unterschrieben von", description: "Unterschrift durch" },
  custom_items: { label: "Sonderausstattung", description: "Zusätzliche Ausstattung" },
  
  // Werkzeuge (employee_tools)
  tool_definition_id: { label: "Werkzeug", description: "Art des Werkzeugs" },
  serial_number: { label: "Seriennummer", description: "Seriennummer des Geräts" },
  inventory_number: { label: "Inventarnummer", description: "Interne Inventarnummer" },
  assigned_date: { label: "Ausgabedatum", description: "Datum der Ausgabe" },
  is_assigned: { label: "Zugewiesen", description: "Aktuell zugewiesen" },
  replacement_cost: { label: "Wiederbeschaffung", description: "Kosten für Ersatz" },
  returned_at: { label: "Rückgabe am", description: "Datum der Rückgabe" },
  return_notes: { label: "Rückgabe-Notiz", description: "Notizen zur Rückgabe" },
  returned_signature_url: { label: "Rückgabe-Unterschrift", description: "Unterschrift bei Rückgabe" },
  returned_signed_by: { label: "Rückgabe durch", description: "Rückgabe quittiert von" },
  
  // Zeiterfassung (employee_time_tracking)
  month: { label: "Monat", description: "Abrechnungsmonat" },
  monthly_hours: { label: "Monatsstunden", description: "Arbeitsstunden im Monat" },
  vacation_days: { label: "Urlaubstage", description: "Genommene Urlaubstage" },
  sick_days: { label: "Krankheitstage", description: "Krankheitstage im Monat" },
  other_absence_days: { label: "Sonstige Abwesenheit", description: "Sonstige Fehltage" },
  other_absence_reason: { label: "Abwesenheitsgrund", description: "Grund für Abwesenheit" },
  
  // Annehmlichkeiten (employee_perks)
  perk_type: { label: "Art", description: "Art der Zuwendung" },
  perk_date: { label: "Datum", description: "Datum der Zuwendung" },
  
  // Zugangsdaten (employee_external_credentials)
  access_type: { label: "Zugangsart", description: "Art des externen Zugangs" },
  username: { label: "Benutzername", description: "Login-Benutzername" },
  password_encrypted: { label: "Passwort", description: "Verschlüsseltes Passwort" },
  
  // Bewerbungen (evaluations)
  total_score: { label: "Gesamtpunktzahl", description: "Erreichte Gesamtpunktzahl" },
  max_score: { label: "Maximalpunktzahl", description: "Maximal erreichbare Punkte" },
  performance_level: { label: "Leistungsstufe", description: "Ermittelte Leistungsstufe" },
  criteria: { label: "Bewertungskriterien", description: "Einzelne Kriterien" },
  date: { label: "Datum", description: "Bewertungsdatum" },
  
  // Allgemeine Felder
  created_at: { label: "Erstellt am", description: "Erstellungszeitpunkt" },
  updated_at: { label: "Geändert am", description: "Letzte Änderung" },
  created_by: { label: "Erstellt von", description: "Erstellt durch Benutzer" },
  employee_id: { label: "Mitarbeiter", description: "Zugeordneter Mitarbeiter" },
};

/**
 * Formatiert einen Datenbank-Feldnamen in eine lesbare deutsche Bezeichnung
 */
const formatFieldName = (fieldName: string): string => {
  const mapping = FIELD_LABEL_MAP[fieldName];
  if (mapping) {
    return mapping.label;
  }
  // Fallback: snake_case zu Title Case konvertieren
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Formatiert eine Liste von geänderten Feldern mit deutschen Labels
 */
const formatChangedFields = (fields: string[]): React.ReactNode => {
  if (!fields || fields.length === 0) return '-';
  
  return (
    <div className="flex flex-wrap gap-1">
      {fields.map((field, index) => {
        const mapping = FIELD_LABEL_MAP[field];
        return (
          <span 
            key={index}
            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-muted/50 border border-border/50"
            title={mapping ? `${field}: ${mapping.description || mapping.label}` : field}
          >
            {formatFieldName(field)}
          </span>
        );
      })}
    </div>
  );
};

export function AuditLogSettings() {
  const { userRole } = useAuth();
  const [activeTab, setActiveTab] = useState("entity");
  
  // Document Access Logs State
  const [docLogs, setDocLogs] = useState<DocumentAccessLogEntry[]>([]);
  const [docLoading, setDocLoading] = useState(true);
  const [docActionFilter, setDocActionFilter] = useState<string>("all");
  const [docTypeFilter, setDocTypeFilter] = useState<string>("all");
  const [docRoleFilter, setDocRoleFilter] = useState<string>("all");
  const [docSourceFilter, setDocSourceFilter] = useState<string>("all");
  const [docDateRange, setDocDateRange] = useState<DateRange | undefined>(undefined);
  const [docSortField, setDocSortField] = useState<SortField>('created_at');
  const [docSortDir, setDocSortDir] = useState<SortDirection>('desc');
  const [docPage, setDocPage] = useState(1);
  const [docTotalCount, setDocTotalCount] = useState(0);
  
  // Entity Audit Logs State
  const [entityLogs, setEntityLogs] = useState<EntityAuditLogEntry[]>([]);
  const [entityLoading, setEntityLoading] = useState(true);
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("all");
  const [entityActionFilter, setEntityActionFilter] = useState<string>("all");
  const [employeeFilter, setEmployeeFilter] = useState<string>("all");
  
  // Shared State
  const [profiles, setProfiles] = useState<Record<string, string>>({});
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(50);

  const canViewLogs = userRole === 'admin' || userRole === 'hr_manager';

  // Fetch user profiles
  const fetchProfiles = async (userIds: string[]) => {
    if (userIds.length === 0) return;
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', userIds);

    const profileMap: Record<string, string> = { ...profiles };
    (data || []).forEach((profile: UserProfile) => {
      profileMap[profile.id] = profile.full_name || 'Unbekannt';
    });
    setProfiles(profileMap);
  };

  // Fetch employees for filter
  const fetchEmployees = async () => {
    const { data } = await supabase
      .from('employees')
      .select('id, name')
      .order('name');
    setEmployees(data || []);
  };

  // Fetch Document Access Logs with pagination
  const fetchDocLogs = async () => {
    if (!canViewLogs) return;
    setDocLoading(true);
    try {
      // First get total count
      let countQuery = supabase
        .from('document_access_log')
        .select('*', { count: 'exact', head: true });

      if (docActionFilter !== 'all') {
        countQuery = countQuery.eq('action', docActionFilter);
      }
      if (docTypeFilter !== 'all') {
        countQuery = countQuery.eq('document_type', docTypeFilter);
      }
      if (docRoleFilter !== 'all') {
        countQuery = countQuery.eq('user_role', docRoleFilter);
      }
      if (docSourceFilter !== 'all') {
        countQuery = countQuery.eq('source', docSourceFilter);
      }
      if (docDateRange?.from) {
        countQuery = countQuery.gte('created_at', startOfDay(docDateRange.from).toISOString());
      }
      if (docDateRange?.to) {
        countQuery = countQuery.lte('created_at', endOfDay(docDateRange.to).toISOString());
      }

      const { count } = await countQuery;
      setDocTotalCount(count || 0);

      // Then fetch paginated data
      const offset = (docPage - 1) * pageSize;
      let query = supabase
        .from('document_access_log')
        .select('*')
        .order(docSortField, { ascending: docSortDir === 'asc' })
        .range(offset, offset + pageSize - 1);

      if (docActionFilter !== 'all') {
        query = query.eq('action', docActionFilter);
      }
      if (docTypeFilter !== 'all') {
        query = query.eq('document_type', docTypeFilter);
      }
      if (docRoleFilter !== 'all') {
        query = query.eq('user_role', docRoleFilter);
      }
      if (docSourceFilter !== 'all') {
        query = query.eq('source', docSourceFilter);
      }
      if (docDateRange?.from) {
        query = query.gte('created_at', startOfDay(docDateRange.from).toISOString());
      }
      if (docDateRange?.to) {
        query = query.lte('created_at', endOfDay(docDateRange.to).toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      setDocLogs(data || []);
      const userIds = [...new Set((data || []).map(log => log.user_id))];
      await fetchProfiles(userIds);
    } catch (error) {
      console.error('Error fetching document logs:', error);
    } finally {
      setDocLoading(false);
    }
  };

  // Fetch Entity Audit Logs
  const fetchEntityLogs = async () => {
    if (!canViewLogs) return;
    setEntityLoading(true);
    try {
      let query = supabase
        .from('entity_audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(pageSize);

      if (entityTypeFilter !== 'all') {
        query = query.eq('entity_type', entityTypeFilter);
      }
      if (entityActionFilter !== 'all') {
        query = query.eq('action', entityActionFilter);
      }
      if (employeeFilter !== 'all') {
        query = query.eq('employee_id', employeeFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      setEntityLogs(data || []);
      const userIds = [...new Set((data || []).filter(log => log.user_id).map(log => log.user_id!))];
      await fetchProfiles(userIds);
    } catch (error) {
      console.error('Error fetching entity logs:', error);
    } finally {
      setEntityLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (activeTab === 'documents') {
      fetchDocLogs();
    }
  }, [docActionFilter, docTypeFilter, docRoleFilter, docSourceFilter, docDateRange, docSortField, docSortDir, docPage, pageSize, canViewLogs, activeTab]);

  useEffect(() => {
    if (activeTab === 'entity') {
      fetchEntityLogs();
    }
  }, [entityTypeFilter, entityActionFilter, employeeFilter, pageSize, canViewLogs, activeTab]);

  // Reset page when filters change
  useEffect(() => {
    setDocPage(1);
  }, [docActionFilter, docTypeFilter, docRoleFilter, docSourceFilter, docDateRange, pageSize]);

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (docSortField === field) {
      setDocSortDir(docSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setDocSortField(field);
      setDocSortDir('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (docSortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return docSortDir === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1" /> 
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Quick date presets
  const setDatePreset = (preset: string) => {
    const today = new Date();
    switch (preset) {
      case 'today':
        setDocDateRange({ from: today, to: today });
        break;
      case 'yesterday':
        const yesterday = subDays(today, 1);
        setDocDateRange({ from: yesterday, to: yesterday });
        break;
      case 'week':
        setDocDateRange({ from: subDays(today, 7), to: today });
        break;
      case 'month':
        setDocDateRange({ from: subMonths(today, 1), to: today });
        break;
      case 'all':
        setDocDateRange(undefined);
        break;
    }
  };

  // CSV Export (metadata only - GDPR compliant)
  const exportToCSV = () => {
    const headers = ['Zeitpunkt', 'Benutzer', 'Rolle', 'Aktion', 'Dokumenttyp', 'Quelle', 'Dokument-ID'];
    const rows = filteredDocLogs.map(log => [
      format(new Date(log.created_at), "dd.MM.yyyy HH:mm:ss", { locale: de }),
      profiles[log.user_id] || 'Unbekannt',
      log.user_role || 'System',
      log.action,
      log.document_type,
      log.source,
      log.document_id
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `audit-dokumentenzugriffe-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Badge Helpers
  const getDocActionBadge = (action: string) => {
    switch (action) {
      case 'preview_opened':
        return <Badge variant="default" className="gap-1"><Eye className="h-3 w-3" /> Vorschau</Badge>;
      case 'preview_failed':
        return <Badge variant="destructive" className="gap-1"><EyeOff className="h-3 w-3" /> Fehlgeschlagen</Badge>;
      case 'download_requested':
        return <Badge variant="secondary" className="gap-1"><Download className="h-3 w-3" /> Download</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const getEntityActionBadge = (action: string) => {
    switch (action) {
      case 'created':
        return <Badge className="gap-1 bg-green-500/20 text-green-400 border-green-500/30"><Plus className="h-3 w-3" /> Erstellt</Badge>;
      case 'updated':
        return <Badge className="gap-1 bg-blue-500/20 text-blue-400 border-blue-500/30"><Pencil className="h-3 w-3" /> Geändert</Badge>;
      case 'deleted':
        return <Badge className="gap-1 bg-red-500/20 text-red-400 border-red-500/30"><Trash2 className="h-3 w-3" /> Gelöscht</Badge>;
      case 'uploaded':
        return <Badge className="gap-1 bg-purple-500/20 text-purple-400 border-purple-500/30"><Upload className="h-3 w-3" /> Hochgeladen</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const getEntityTypeBadge = (entityType: string) => {
    const typeMap: Record<string, { icon: React.ReactNode; label: string; className: string }> = {
      employees: { icon: <User className="h-3 w-3" />, label: 'Stammdaten', className: 'bg-primary/20 text-primary border-primary/30' },
      salary_history: { icon: <DollarSign className="h-3 w-3" />, label: 'Gehalt', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
      bonus_history: { icon: <Gift className="h-3 w-3" />, label: 'Bonus', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
      benefits_history: { icon: <Shield className="h-3 w-3" />, label: 'Benefits', className: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
      employee_documents: { icon: <FileText className="h-3 w-3" />, label: 'Dokumente', className: 'bg-violet-500/20 text-violet-400 border-violet-500/30' },
      employee_clothing: { icon: <Shirt className="h-3 w-3" />, label: 'Kleidung', className: 'bg-pink-500/20 text-pink-400 border-pink-500/30' },
      employee_tools: { icon: <Wrench className="h-3 w-3" />, label: 'Werkzeuge', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      employee_time_tracking: { icon: <Clock className="h-3 w-3" />, label: 'Zeiterfassung', className: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
      employee_perks: { icon: <Gift className="h-3 w-3" />, label: 'Annehmlichkeiten', className: 'bg-rose-500/20 text-rose-400 border-rose-500/30' },
      employee_external_credentials: { icon: <Shield className="h-3 w-3" />, label: 'Zugangsdaten', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
      applicant_documents: { icon: <FileText className="h-3 w-3" />, label: 'Bewerber-Dok.', className: 'bg-teal-500/20 text-teal-400 border-teal-500/30' },
    };
    const config = typeMap[entityType] || { icon: null, label: entityType, className: '' };
    return <Badge className={`gap-1 ${config.className}`}>{config.icon} {config.label}</Badge>;
  };

  const getRoleBadge = (role: string | null) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Admin</Badge>;
      case 'hr_manager':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">HR-Manager</Badge>;
      case 'hr_clerk':
        return <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30">Personalverwalter</Badge>;
      case 'employee':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Mitarbeiter</Badge>;
      default:
        return <Badge variant="outline">{role || 'System'}</Badge>;
    }
  };

  const getEmployeeName = (employeeId: string | null) => {
    if (!employeeId) return '-';
    const emp = employees.find(e => e.id === employeeId);
    return emp?.name || employeeId.substring(0, 8) + '...';
  };

  // Filter logs by search term
  const filteredDocLogs = useMemo(() => {
    return docLogs.filter(log => {
      if (!searchTerm) return true;
      const userName = profiles[log.user_id] || '';
      return userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.document_id.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [docLogs, searchTerm, profiles]);

  const filteredEntityLogs = useMemo(() => {
    return entityLogs.filter(log => {
      if (!searchTerm) return true;
      const userName = log.user_id ? (profiles[log.user_id] || '') : '';
      const empName = getEmployeeName(log.employee_id);
      return userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        empName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [entityLogs, searchTerm, profiles, employees]);

  // Pagination calculations
  const totalPages = Math.ceil(docTotalCount / pageSize);

  if (!canViewLogs) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Zugriff verweigert</CardTitle>
          <CardDescription>
            Sie haben keine Berechtigung, die Audit-Logs einzusehen.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Audit-Protokoll
          </CardTitle>
          <CardDescription>
            DSGVO-konforme Übersicht aller Datenänderungen und Dokumentenzugriffe. Nur Metadaten – keine Inhalte.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="entity" className="gap-2">
                <Pencil className="h-4 w-4" />
                Datenänderungen
              </TabsTrigger>
              <TabsTrigger value="documents" className="gap-2">
                <Eye className="h-4 w-4" />
                Dokumentenzugriffe
              </TabsTrigger>
            </TabsList>

            {/* Entity Audit Log Tab */}
            <TabsContent value="entity" className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Suche nach Benutzer, Mitarbeiter..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Bereich" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Bereiche</SelectItem>
                    <SelectItem value="employees">Stammdaten</SelectItem>
                    <SelectItem value="salary_history">Gehalt</SelectItem>
                    <SelectItem value="bonus_history">Bonus</SelectItem>
                    <SelectItem value="benefits_history">Benefits</SelectItem>
                    <SelectItem value="employee_documents">Dokumente</SelectItem>
                    <SelectItem value="employee_clothing">Kleidung</SelectItem>
                    <SelectItem value="employee_tools">Werkzeuge</SelectItem>
                    <SelectItem value="employee_time_tracking">Zeiterfassung</SelectItem>
                    <SelectItem value="employee_perks">Annehmlichkeiten</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={entityActionFilter} onValueChange={setEntityActionFilter}>
                  <SelectTrigger className="w-full sm:w-[150px]">
                    <SelectValue placeholder="Aktion" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Aktionen</SelectItem>
                    <SelectItem value="created">Erstellt</SelectItem>
                    <SelectItem value="updated">Geändert</SelectItem>
                    <SelectItem value="deleted">Gelöscht</SelectItem>
                    <SelectItem value="uploaded">Hochgeladen</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Mitarbeiter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Mitarbeiter</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(parseInt(v))}>
                  <SelectTrigger className="w-full sm:w-[120px]">
                    <SelectValue placeholder="Anzahl" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={fetchEntityLogs} disabled={entityLoading}>
                  <RefreshCw className={`h-4 w-4 ${entityLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Gesamt</p>
                  <p className="text-2xl font-bold">{filteredEntityLogs.length}</p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Erstellt</p>
                  <p className="text-2xl font-bold text-green-400">
                    {filteredEntityLogs.filter(l => l.action === 'created').length}
                  </p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Geändert</p>
                  <p className="text-2xl font-bold text-blue-400">
                    {filteredEntityLogs.filter(l => l.action === 'updated').length}
                  </p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Gelöscht</p>
                  <p className="text-2xl font-bold text-red-400">
                    {filteredEntityLogs.filter(l => l.action === 'deleted').length}
                  </p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Hochgeladen</p>
                  <p className="text-2xl font-bold text-purple-400">
                    {filteredEntityLogs.filter(l => l.action === 'uploaded').length}
                  </p>
                </Card>
              </div>

              {/* Table */}
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Zeitpunkt</TableHead>
                      <TableHead>Benutzer</TableHead>
                      <TableHead>Rolle</TableHead>
                      <TableHead>Bereich</TableHead>
                      <TableHead>Aktion</TableHead>
                      <TableHead>Mitarbeiter</TableHead>
                      <TableHead className="hidden lg:table-cell">Geänderte Felder</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entityLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 7 }).map((_, j) => (
                            <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : filteredEntityLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Keine Audit-Einträge gefunden
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredEntityLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(log.created_at), "dd.MM.yyyy HH:mm", { locale: de })}
                          </TableCell>
                          <TableCell className="font-medium">
                            {log.user_id 
                              ? (profiles[log.user_id] || 'Wird geladen...') 
                              : <span className="text-muted-foreground italic">System (automatisch)</span>
                            }
                          </TableCell>
                          <TableCell>{getRoleBadge(log.user_role)}</TableCell>
                          <TableCell>{getEntityTypeBadge(log.entity_type)}</TableCell>
                          <TableCell>{getEntityActionBadge(log.action)}</TableCell>
                          <TableCell>{getEmployeeName(log.employee_id)}</TableCell>
                          <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-[300px]">
                            {(() => {
                              const changes = log.changes as { changed_fields?: string[]; action_detail?: string } | null;
                              if (changes?.changed_fields && changes.changed_fields.length > 0) {
                                return formatChangedFields(changes.changed_fields);
                              }
                              return changes?.action_detail || '-';
                            })()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Document Access Log Tab */}
            <TabsContent value="documents" className="space-y-4">
              {/* Date Presets */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant={!docDateRange ? "default" : "outline"} 
                  size="sm" 
                  onClick={() => setDatePreset('all')}
                >
                  Alle
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDatePreset('today')}>
                  Heute
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDatePreset('yesterday')}>
                  Gestern
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDatePreset('week')}>
                  Letzte 7 Tage
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDatePreset('month')}>
                  Letzter Monat
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {docDateRange?.from ? (
                        docDateRange.to ? (
                          <>
                            {format(docDateRange.from, "dd.MM.yy")} - {format(docDateRange.to, "dd.MM.yy")}
                          </>
                        ) : (
                          format(docDateRange.from, "dd.MM.yyyy")
                        )
                      ) : (
                        "Zeitraum wählen"
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={docDateRange?.from}
                      selected={docDateRange}
                      onSelect={setDocDateRange}
                      numberOfMonths={2}
                      locale={de}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Suche nach Benutzer, Dokument-ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={docRoleFilter} onValueChange={setDocRoleFilter}>
                  <SelectTrigger className="w-full sm:w-[160px]">
                    <SelectValue placeholder="Rolle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Rollen</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="hr_manager">HR-Manager</SelectItem>
                    <SelectItem value="hr_clerk">Personalverwalter</SelectItem>
                    <SelectItem value="employee">Mitarbeiter</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={docActionFilter} onValueChange={setDocActionFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Aktion" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Aktionen</SelectItem>
                    <SelectItem value="preview_opened">Vorschau geöffnet</SelectItem>
                    <SelectItem value="preview_failed">Vorschau fehlgeschlagen</SelectItem>
                    <SelectItem value="download_requested">Download angefordert</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={docTypeFilter} onValueChange={setDocTypeFilter}>
                  <SelectTrigger className="w-full sm:w-[160px]">
                    <SelectValue placeholder="Dokumenttyp" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Typen</SelectItem>
                    <SelectItem value="employee">Mitarbeiter</SelectItem>
                    <SelectItem value="applicant">Bewerber</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={docSourceFilter} onValueChange={setDocSourceFilter}>
                  <SelectTrigger className="w-full sm:w-[140px]">
                    <SelectValue placeholder="Quelle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Quellen</SelectItem>
                    <SelectItem value="modal">Modal</SelectItem>
                    <SelectItem value="preview_route">Vorschau-Route</SelectItem>
                    <SelectItem value="download_button">Download-Button</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(parseInt(v))}>
                  <SelectTrigger className="w-full sm:w-[100px]">
                    <SelectValue placeholder="Anzahl" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={fetchDocLogs} disabled={docLoading}>
                  <RefreshCw className={`h-4 w-4 ${docLoading ? 'animate-spin' : ''}`} />
                </Button>
                <Button variant="outline" size="sm" onClick={exportToCSV} className="gap-2">
                  <Download className="h-4 w-4" />
                  CSV
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Gesamt</p>
                  <p className="text-2xl font-bold">{docTotalCount}</p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Vorschauen</p>
                  <p className="text-2xl font-bold text-primary">
                    {filteredDocLogs.filter(l => l.action === 'preview_opened').length}
                  </p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Downloads</p>
                  <p className="text-2xl font-bold text-secondary-foreground">
                    {filteredDocLogs.filter(l => l.action === 'download_requested').length}
                  </p>
                </Card>
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground">Fehlgeschlagen</p>
                  <p className="text-2xl font-bold text-destructive">
                    {filteredDocLogs.filter(l => l.action === 'preview_failed').length}
                  </p>
                </Card>
              </div>

              {/* Table */}
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center">
                          Zeitpunkt {getSortIcon('created_at')}
                        </div>
                      </TableHead>
                      <TableHead>Benutzer</TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('user_role')}
                      >
                        <div className="flex items-center">
                          Rolle {getSortIcon('user_role')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('action')}
                      >
                        <div className="flex items-center">
                          Aktion {getSortIcon('action')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('document_type')}
                      >
                        <div className="flex items-center">
                          Dokumenttyp {getSortIcon('document_type')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('source')}
                      >
                        <div className="flex items-center">
                          Quelle {getSortIcon('source')}
                        </div>
                      </TableHead>
                      <TableHead className="hidden lg:table-cell">Dokument-ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {docLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 7 }).map((_, j) => (
                            <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : filteredDocLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Keine Zugriffs-Einträge gefunden
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDocLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(log.created_at), "dd.MM.yyyy HH:mm", { locale: de })}
                          </TableCell>
                          <TableCell className="font-medium">
                            {profiles[log.user_id] || 'Unbekannt'}
                          </TableCell>
                          <TableCell>{getRoleBadge(log.user_role)}</TableCell>
                          <TableCell>{getDocActionBadge(log.action)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="gap-1">
                              {log.document_type === 'employee' ? <User className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                              {log.document_type === 'employee' ? 'Mitarbeiter' : 'Bewerber'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {log.source.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell font-mono text-xs text-muted-foreground">
                            {log.document_id.substring(0, 8)}...
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Zeige {((docPage - 1) * pageSize) + 1} - {Math.min(docPage * pageSize, docTotalCount)} von {docTotalCount} Einträgen
                  </p>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setDocPage(p => Math.max(1, p - 1))}
                      disabled={docPage === 1 || docLoading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Seite {docPage} von {totalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setDocPage(p => Math.min(totalPages, p + 1))}
                      disabled={docPage === totalPages || docLoading}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* GDPR Info Footer */}
          <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
            <p className="font-medium mb-1">📋 DSGVO-Hinweis</p>
            <p>
              Dieses Protokoll speichert ausschließlich Metadaten (Benutzer-ID, Zeitstempel, geänderte Feldnamen). 
              Es werden keine Dateiinhalte, personenbezogenen Werte oder sensiblen Daten erfasst.
              Die Logs sind unveränderlich und dienen der Nachvollziehbarkeit gem. Art. 5 Abs. 2 DSGVO.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
